import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAuth } from "firebase/auth";

function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sr-RS");
}

function getIcon(action) {
  switch (action) {
    case "task_status_changed": return "🔄";
    case "task_assigned": return "👤";
    case "task_created": return "🆕";
    case "resource_updated": return "🧩";
    default: return "📌";
  }
}

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getActionBadgeClass(actionType) {
  switch (actionType) {
    case "task_assigned":
      return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900";
    case "task_status_changed":
      return "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900";
    case "task_created":
      return "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-900";
    case "resource_updated":
      return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700";
  }
}

export default function ProjectActivityTimeline({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  async function load() {
    try {
      setLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !projectId) return;

      const token = await user.getIdToken();

      const res = await axios.get(
        `https://modularobjects.com/checkout/api/project/${projectId}/activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setItems(res.data?.items || []);
    } catch (err) {
      console.error("Failed to load project activity:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (projectId) load();
  }, [projectId]);

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-black dark:text-white">
          Activity Timeline
        </h2>
        <p className="text-sm text-gray-500 dark:text-neutral-400">
          Latest changes and workflow events on this project.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-neutral-400">Loading activity...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-neutral-400">No activity yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-blue-500 mt-1" />
                <div className="flex-1 w-px bg-gray-200 dark:bg-neutral-700 mt-2" />
              </div>

              <div className="flex-1 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getActionBadgeClass(item.action_type)}`}>
                    {getIcon(item.action_type)}
                  </span>
                  <span className="font-medium text-black dark:text-white">
                    {item.title || "Activity"}
                  </span>
                </div>

                {item.message ? (
                  <div className="mt-2 text-sm text-gray-700 dark:text-neutral-300">
                    {item.message}
                  </div>
                ) : null}

                {item.meta?.status && (
  <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
    {item.meta.status}
  </span>
)}

{item.meta?.priority && (
  <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 ml-2">
    {item.meta.priority}
  </span>
)}

                <div className="mt-2 text-xs text-gray-500 dark:text-neutral-400">
                  {timeAgo(item.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}